import { useEffect, useState } from 'react';
import GTable from '../../../../../components/elements/GTable';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useRoute from '../../../../../hooks/routesHook';
import useUserAccount from '../../../../../hooks/userAccountHook';
import { Payout } from '../../../../../models/payout/Payout';
import { useQuery } from '@tanstack/react-query';
import useSettlements from '../../../../../hooks/settlementHook';
import { Settlement } from '../../../../../models/settlement/Settlement';
import { ITableFilterItem } from '../../../../../resources/interfaces';
import { onRemoveFilter } from '../../../../../utils/miscFunctions';
import React from 'react';

interface ISettlementTableProps {
    showTitleSection?: boolean;
    selectedBrokerFromParent: any;
    selectedInsurerFromParent: number;
    fromDate: string;
    toDate: string;
    isPaid: string;
    isApproved: boolean;
    reviewStage: string;
    tableFilters?: ITableFilterItem[];
    setTableFilters: (filters: ITableFilterItem[]) => void;
    invalidateQuery?: boolean;
}

const SettlementsTable = ({ showTitleSection, selectedBrokerFromParent, selectedInsurerFromParent, toDate, fromDate, isPaid, isApproved, reviewStage, tableFilters, setTableFilters, invalidateQuery }: ISettlementTableProps) => {
    const { user } = useUserAccount()
    const [showPerPage, setShowPerPage] = useState<number>(25);
    const { getDashboardBase } = useRoute();
    const navigate = useNavigate();

    const [selectedBroker, setSelectedBroker] = useState<string | number>(selectedBrokerFromParent);
    const [selectedInsurer, setSelectedInsurer] = useState<number>(selectedInsurerFromParent);
    const [searchValue, setSearchValue] = useState<string>('');

    const getTableRowActions = (payout: Payout) => {
        const actions = [];

        if (user.isAdministrator() && !payout.isPaid && payout.isReviewed && payout.isApproved) {
            actions.push({
                label: 'Mark as paid',
                callback: () => { }
            });
        }

        return actions;
    };
    const { fetchAllsettlements, mainColumns, currentPage, loading, handlePageChange } = useSettlements({ getTableRowActions })

    useEffect(() => {
        if (selectedBrokerFromParent !== undefined) {
            setSelectedBroker(selectedBrokerFromParent);
        }

        if (selectedInsurerFromParent !== undefined) {
            setSelectedInsurer(selectedInsurerFromParent);
        }
    }, [selectedBrokerFromParent, selectedInsurerFromParent]);

     const { data, isLoading } = useQuery({
        queryKey: ['settlements', currentPage, showPerPage, selectedBroker, selectedInsurer, fromDate, toDate, isPaid, isApproved, reviewStage, searchValue, invalidateQuery],
        queryFn: () => fetchAllsettlements(currentPage, showPerPage, Number(selectedBroker), selectedInsurer, fromDate, toDate, isPaid, isApproved, reviewStage, searchValue),
        staleTime: 1000 * 10,
        placeholderData: (prev) => prev,
         gcTime: 1000 * 60 * 30 * 5,
        select: (response) => {
      
          return {
            settlementsData: response?.content?.map((item: any) => new Settlement(item)),
            pagination: {
              per_page: response?.size,
              total: response?.totalElements,
              totalPages: response?.totalPages,
              from: response?.pageable?.offset + 1,
              to: response?.totalElements > response?.pageable?.offset + showPerPage
                ? response?.pageable?.offset + showPerPage : response?.totalElements,
            },
          };
        },
      });

    const pagination = data?.pagination;
     const settlementsData = React.useMemo(() => {
          return data?.settlementsData?.map((item:any) => new Settlement(item));
        }, [data?.settlementsData]);


    return (
        <div className={`${showTitleSection ? 'bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border overflow-hidden' : ''}`}>
            {showTitleSection && (
                <div className="p-4 border-b border-gray-200 dark:border-darkBg-border">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">Settlements</h2>
                </div>
            )}

            <div className="overflow-x-auto p-4 mt-4">
                <GTable
                    columns={mainColumns}
                    data={settlementsData || []}
                    showPagination
                    searchPlaceholder="Search by reference"
                    paginationData={pagination}
                    onSearch={(searchValue: string) => setSearchValue(searchValue)}
                    tableType={'settlement'}
                    loading={isLoading}
                    showPerPage={showPerPage}
                    onPageChange={handlePageChange}
                    selectedFilters={tableFilters}
                    onShowPerPageChange={(page: number) => setShowPerPage(page)}
                    onRemoveFilter={(filter: ITableFilterItem, index: number) => {
                        onRemoveFilter(filter, index, tableFilters || [], setTableFilters)
                    }}
                    onRowClick={(settlement: Settlement) => { navigate(user.isBroker() ? `${getDashboardBase()}/commissions/settlements/${settlement.id}` : `${getDashboardBase()}/settlements/${settlement.id}`); }}
                    classes={'h-fit'}
                />
            </div>
        </div >
    )

};

export default SettlementsTable;
